// Helper to determine if a user has access to a conversation based on role and relationship
// Used in addConversation (WebSocket events) to filter which conversations to add to the store
// For direct access (getConversation), backend validation is sufficient
//
// IMPORTANT: Uses optional chaining to handle partial WebSocket payloads safely
//
// SECURITY PHILOSOPHY: Trust backend authorization with fail-open strategy
// - Backend is the authoritative source: ActionCable channels validate authorization before sending events
// - If backend sent the event via authorized channel, frontend trusts that decision
// - Frontend filtering is for UX (tab visibility), NOT for security enforcement
//
// FAIL-OPEN STRATEGY (chosen to prevent ghost conversations):
// - Missing metadata (conversation.meta) → ACCEPT (trust backend, incomplete payload ≠ unauthorized)
// - Missing currentUser → REJECT (cannot determine who is making the request)
// - Missing team.is_member → ACCEPT (trust backend authorization, prevents WS event drops)
//
// TRADE-OFF: This prioritizes preventing ghost conversations (real bug with video evidence)
// over theoretical defense-in-depth. If backend has authorization bugs, frontend will reflect them.
// The alternative (fail-closed) would drop WS events with incomplete payloads → state desync → ghost conversations.
export const hasAccessToConversation = (conversation, currentUser) => {
  // Fail-closed: reject if we can't determine WHO is accessing
  if (!currentUser) {
    return false;
  }

  // Fail-open: if meta is missing but currentUser exists, trust backend authorization
  // Backend already validated via ActionCable channel subscription
  // Rejecting here would drop WS events and cause state desync
  if (!conversation?.meta) {
    return true;
  }

  // Always allow if user is the assignee
  if (conversation.meta?.assignee?.id === currentUser.id) {
    return true;
  }

  // Role-based permissions
  if (currentUser.role === 'administrator') {
    return true;
  }

  if (currentUser.role === 'agent') {
    // Allow if conversation is unassigned and has no team
    if (!conversation.meta?.assignee && !conversation.meta?.team) {
      return true;
    }

    // For conversations with a team, check if agent is a team member
    // Fail-open when is_member is undefined (WebSocket payload may be incomplete)
    // Backend channel authorization is the primary security boundary
    if (conversation.meta?.team) {
      return conversation.meta.team.is_member !== false; // Allow if true or undefined
    }

    // If no team but has assignee, deny access unless agent is assignee (already checked above)
    return false;
  }

  if (currentUser.role === 'control' || currentUser.role === 'coordination' || currentUser.role === 'monitoring') {
    // Check if user is a member of the conversation's team
    if (!conversation.meta?.team) {
      return true; // No team assigned
    }
    // Fail-open when is_member is undefined (backend authorization is primary)
    return conversation.meta.team.is_member !== false; // Allow if true or undefined
  }

  return false;
};